const DOMAINS = Object.freeze([
  // International
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'yahoo.com',
  'ymail.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'protonmail.com',
  // Germany
  'yahoo.de',
  'gmx.de',
  'gmx.net',
  'web.de',
  't-online.de',
  'freenet.de',
  '1und1.de',
  'online.de',
  'posteo.de',
  'mailbox.org',
  'arcor.de',
  'vodafone.de',
  'kabelmail.de',
  'magenta.de',
  'outlook.de',
  'mail.de',
  'email.de',
  'o2online.de',
  // Austria
  'gmx.at',
  'a1.at',
  'drei.at',
  // Switzerland
  'gmx.ch',
  'bluewin.ch',
  'sunrise.ch',
  // Russia
  'mail.ru',
  'yandex.ru',
]);
const TLDS = new Set(['com', 'net', 'org', 'de', 'at', 'ch', 'ru']);

const INVALID_CHARS_REGEX = /[<>"'`&\\]/g;

export const getSuggestedEmail = (email) => {
  if (!email || typeof email !== 'string' || !email.includes('@')) return null;

  const sanitized = email.replace(INVALID_CHARS_REGEX, '').trim();
  const atIndex = sanitized.lastIndexOf('@');
  if (atIndex < 1) return null;

  const localPart = sanitized.slice(0, atIndex);
  const domain = sanitized
    .slice(atIndex + 1)
    .replace(/\.$/, '')
    .toLowerCase();

  if (!localPart || !domain || domain.length < 2) return null;
  if (DOMAINS.includes(domain)) return null;

  const suggestedDomain = findClosestDomain(domain);

  return suggestedDomain
    ? { suggested: `${localPart}@${suggestedDomain}` }
    : null;
};

const findClosestDomain = (domain) => {
  // Prefix match win
  const prefixMatches = DOMAINS.filter((d) => d.startsWith(domain));
  if (prefixMatches.length === 1) {
    return prefixMatches[0];
  }
  if (prefixMatches.length > 1) {
    return prefixMatches.sort((a, b) => a.length - b.length)[0];
  }

  // Calculate closest match
  let bestMatch = null;
  let bestScore = Infinity;

  for (const target of DOMAINS) {
    const score = calculateWeightedScore(domain, target);
    if (score < bestScore && score <= MAX_TOTAL_SCORE) {
      bestScore = score;
      bestMatch = target;
    }
  }

  return bestMatch;
};

// We split the domain into provider and TLD parts, then calculate the distance
// to the closest match for the provider part, but also for the full domain.
// The TLD part is evaluated separately, taking substitutions into account.
// We then apply weights to each, based on their importance.
// The match with the lowest score is then used as the most probable suggestion.
const WEIGHTS = {
  provider: 3, // Provider identity matters more than anything else
  tld: 1,
  full: 0.25,
};
const MAX_PROVIDER_DISTANCE = 2;
const MAX_TOTAL_SCORE = 7; // Avoid making a suggestion that's too far away.

const calculateWeightedScore = (inputDomain, targetDomain) => {
  const input = splitDomain(inputDomain);
  const target = splitDomain(targetDomain);
  if (!input || !target) return Infinity;

  const providerDistance = calculateLevenshteinDistance(
    input.provider,
    target.provider
  );

  if (providerDistance > MAX_PROVIDER_DISTANCE) return Infinity;

  const tldScore = calculateTldScore(input.tld, target.tld);

  const fullDistance = calculateLevenshteinDistance(inputDomain, targetDomain);

  return (
    providerDistance * WEIGHTS.provider +
    tldScore * WEIGHTS.tld +
    fullDistance * WEIGHTS.full
  );
};

// Implementation of the Levenshtein distance algorithm.
// See https://en.wikipedia.org/wiki/Levenshtein_distance
// See also the leven library as reference https://www.npmjs.com/package/leven
const calculateLevenshteinDistance = (a, b) => {
  if (a === b) return 0;
  if (!a) return b.length;
  if (!b) return a.length;

  if (a.length > b.length) [a, b] = [b, a];

  const row = new Uint8Array(a.length + 1);
  for (let i = 0; i <= a.length; i++) row[i] = i;

  for (let i = 1; i <= b.length; i++) {
    let prev = i;
    for (let j = 1; j <= a.length; j++) {
      const curr =
        b.charCodeAt(i - 1) === a.charCodeAt(j - 1)
          ? row[j - 1]
          : Math.min(row[j - 1], prev, row[j]) + 1;
      row[j - 1] = prev;
      prev = curr;
    }
    row[a.length] = prev;
  }

  return row[a.length];
};

const calculateTldScore = (inputTld, targetTld) => {
  if (inputTld === targetTld) return 0;

  if (TLDS.has(inputTld) && TLDS.has(targetTld)) {
    return 1; // TLD substitution
  }

  return 2; // unrelated TLD
};

const splitDomain = (domain) => {
  const parts = domain.split('.');
  if (parts.length < 2) return null;

  const tld = parts.pop();
  const provider = parts.join('.');

  return { provider, tld };
};
